import React from 'react';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { LOCALES } from '../../../config/locales';
import { ValidationErrors } from '../../../hooks/useForm';
import { RoutineDurationExercise, RoutineExercise, RoutineRepetitionExercise } from '../../../types/Fisiofit/routine';
import { EXERCISE_TYPE, ExerciseShort } from '../../../types/Fisiofit/exercise';
import { MUSCLE_GROUPS, EXERCISE_CATEGORIES } from '../../../types/Fisiofit/fisiofitConst';
import {
  canCreateSupersetWithNext,
  canCreateSupersetWithPrevious,
  cannotCreateSupersetWithNext,
  cannotCreateSupersetWithPrevious,
} from '../../../utils/supersetUtils';
import { ExerciseViewIcon } from '../../ExerciseViewIcon/ExerciseViewIcon';
import { RoutineForm } from '../validators';
import { RoutineExercisesActions, RoutineSupersetsActions } from '../types';
import { ExerciseDetails } from './ExerciseDetails';
import { SingleExerciseFields } from './SingleExerciseFields';

interface SingleExerciseItemProps {
  exercise: RoutineExercise;
  dayIndex: number;
  exerciseIndex: number;
  isExpanded: boolean;
  isDragging: boolean | undefined;
  isDuplicate: boolean;
  shouldDisableInputs: boolean;
  provided?: any;
  form: RoutineForm;
  isEditing: boolean;
  isCreating: boolean;
  validationErrors: ValidationErrors<RoutineForm> | undefined;
  locale: LOCALES;
  organizationExercises: ExerciseShort[] | undefined;
  selectedExerciseMuscleGroups: MUSCLE_GROUPS[];
  selectedExerciseCategories: EXERCISE_CATEGORIES[];
  isLoadingExercisePreloadValues: boolean;
  exercises: RoutineExercisesActions;
  supersets: RoutineSupersetsActions;
}

export const SingleExerciseItem = ({
  exercise: singleExercise,
  dayIndex,
  exerciseIndex,
  isExpanded,
  isDragging,
  isDuplicate,
  shouldDisableInputs,
  provided,
  form,
  isEditing,
  isCreating,
  validationErrors,
  locale,
  organizationExercises,
  selectedExerciseMuscleGroups,
  selectedExerciseCategories,
  isLoadingExercisePreloadValues,
  exercises,
  supersets,
}: SingleExerciseItemProps) => {
  const isRepetitionExercise = singleExercise.exerciseType === EXERCISE_TYPE.REPS;
  const canLinkWithNext = canCreateSupersetWithNext(form.exerciseDays[dayIndex].exercises, exerciseIndex);
  const canLinkWithPrev = canCreateSupersetWithPrevious(form.exerciseDays[dayIndex].exercises, exerciseIndex);
  const cannotLinkWithNext = cannotCreateSupersetWithNext(form.exerciseDays[dayIndex].exercises, exerciseIndex);
  const cannotLinkWithPrev = cannotCreateSupersetWithPrevious(form.exerciseDays[dayIndex].exercises, exerciseIndex);

  return (
    <div
      className={classNames('exercise-item', {
        expanded: isExpanded,
        'is-dragging': isDragging,
        'can-create-superset-next': canLinkWithNext,
        'can-create-superset-prev': canLinkWithPrev,
        'has-disabled-superset-next': cannotLinkWithNext,
        'has-disabled-superset-prev': cannotLinkWithPrev,
        'invalid-exercise': validationErrors?.exerciseDays && !singleExercise.exerciseId,
        'duplicate-exercise': isDuplicate,
      })}
      ref={provided?.innerRef}
      {...provided?.draggableProps}
    >
      {/* Botón para crear superserie con ejercicio anterior - posicionado arriba */}
      {(isEditing || isCreating) && canLinkWithPrev && (
        <Button
          icon='pi pi-link'
          text
          rounded
          className='create-superset-button-top'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            supersets.handleCreateSuperset(dayIndex, exerciseIndex, 'previous');
          }}
        />
      )}

      {/* Botón deshabilitado para mostrar que no se puede vincular con el anterior */}
      {(isEditing || isCreating) && cannotLinkWithPrev && (
        <Button
          icon='pi pi-ban'
          text
          rounded
          disabled
          className='create-superset-button-top disabled-superset-button'
          title='No se puede vincular una superserie con un ejercicio individual'
        />
      )}

      <div className='exercise-header'>
        {(isEditing || isCreating) && provided && (
          <div className='drag-handle-exercise' {...provided.dragHandleProps}>
            <i className='pi pi-bars'></i>
          </div>
        )}
        {singleExercise.comment && (
          <div className='comment-indicator'>
            <i className='pi pi-comment'></i>
          </div>
        )}
        <ExerciseViewIcon exercise={singleExercise} />
        <div className='exercise-info'>
          <span className='exercise-name'>{singleExercise.name || 'Nuevo ejercicio'}</span>
        </div>
        {isEditing || isCreating ? (
          <div className='exercise-actions'>
            <Button
              icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
              text
              rounded
              className='expand-button'
              onClick={(e) => exercises.toggleExerciseExpand(dayIndex, exerciseIndex, e)}
            />
            <Button
              icon='pi pi-trash'
              text
              rounded
              className='p-button-danger'
              onClick={(e) => exercises.removeExercise(dayIndex, exerciseIndex, e)}
            />
          </div>
        ) : (
          <div className='exercise-actions'>
            <Button
              icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
              text
              rounded
              className='expand-button'
              onClick={(e) => exercises.toggleExerciseExpand(dayIndex, exerciseIndex, e)}
            />
          </div>
        )}
      </div>

      {/* Botón para crear superserie con ejercicio siguiente - posicionado abajo */}
      {(isEditing || isCreating) && canLinkWithNext && (
        <Button
          icon='pi pi-link'
          text
          rounded
          className='create-superset-button-bottom'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            supersets.handleCreateSuperset(dayIndex, exerciseIndex, 'next');
          }}
        />
      )}

      {/* Botón deshabilitado para mostrar que no se puede vincular con el siguiente */}
      {(isEditing || isCreating) && cannotLinkWithNext && (
        <Button
          icon='pi pi-ban'
          text
          rounded
          disabled
          className='create-superset-button-bottom disabled-superset-button'
          title='No se puede vincular una superserie con un ejercicio individual'
        />
      )}

      {isExpanded && (
        <div className='exercise-edit-panel'>
          {!isEditing && !isCreating && (
            <ExerciseDetails exercise={singleExercise as RoutineRepetitionExercise | RoutineDurationExercise} />
          )}

          {(isEditing || isCreating) && (
            <SingleExerciseFields
              singleExercise={singleExercise}
              dayIndex={dayIndex}
              exerciseIndex={exerciseIndex}
              isDuplicate={isDuplicate}
              shouldDisableInputs={shouldDisableInputs}
              isRepetitionExercise={isRepetitionExercise}
              validationErrors={validationErrors}
              locale={locale}
              organizationExercises={organizationExercises}
              selectedExerciseMuscleGroups={selectedExerciseMuscleGroups}
              selectedExerciseCategories={selectedExerciseCategories}
              isLoadingExercisePreloadValues={isLoadingExercisePreloadValues}
              exercises={exercises}
            />
          )}
        </div>
      )}
    </div>
  );
};
